import { BrandfolderHexColors, useDrop } from '@brandfolder/react';
import { handleAttachmentDragEvent, isAttachmentDragEvent } from '@brandfolder-panel/sdk/utilities';

import classNames from 'classnames';
import { FunctionComponent, HTMLAttributes, useContext, useRef } from 'react';
import styled from 'styled-components';

import { AsideContext } from '@context/aside';
import { BlockContext } from '@context/blocks';
import { DragAndDropContext } from '@context/drag-and-drop';
import { LocaleContext } from '@context/locale';
import { PageContext } from '@context/page';
import { PermissionLevelContext } from '@context/permission-level';
import { PreviewContext } from '@context/preview';
import { PublishContext } from '@context/publish';
import { ThemeContext } from '@context/theme';
import { BlockDragAndDropFormats, BlockTypes } from '@enums/blocks';
import { usePanelSdkAttachmentHandlers } from '@hooks/use-panel-sdk-attachment-handlers';
import { drop, dropDragging, dropOver } from '@styles/drop';
import { BlockDragItem, BlockReorderItem } from '@typings/block';
import {
  createBlock,
  getIsBlockReorderItem,
  insertBlock,
  moveBlock,
  updateBlock
} from '@utilities/block';

interface CanvasDropProps extends HTMLAttributes<HTMLDivElement> {
  position: number;
  parentKey?: string;
}

const UnstyledCanvasDrop: FunctionComponent<CanvasDropProps> = (props) => {
  const { parentKey, position, ...otherProps } = props;

  const ref = useRef<HTMLDivElement | null>(null);

  const { setShowBlocks } = useContext(AsideContext);
  const { blocks, setBlocks } = useContext(BlockContext);
  const { dragging, reordering, setDragging, setReordering } = useContext(DragAndDropContext);
  const { locale } = useContext(LocaleContext);
  const { page } = useContext(PageContext);
  const { preview } = useContext(PreviewContext);
  const { editable } = useContext(PermissionLevelContext);
  const { publishing, setPublish } = useContext(PublishContext);
  const { theme } = useContext(ThemeContext);
  const { addAttachmentBlock } = usePanelSdkAttachmentHandlers();

  const onDropped = (data: BlockDragItem | BlockReorderItem, e: DragEvent, format: string) => {
    if (format === BlockDragAndDropFormats.ImageDrag && isAttachmentDragEvent(e)) {
      handleAttachmentDragEvent(e, ({ attachment }) => {
        setDragging(false);
        addAttachmentBlock(attachment, parentKey, position);
      });
    } else if (
      format === BlockDragAndDropFormats.BlockDrag &&
      !getIsBlockReorderItem(data) &&
      !isAttachmentDragEvent(e)
    ) {
      const block = createBlock({
        ...(data as BlockDragItem),
        locale,
        pageKey: page?.key as string,
        parentKey,
        position
      });

      setBlocks([...insertBlock(block, blocks, page?.key as string)]);
      setDragging(false);
      setPublish(true);
      setShowBlocks(false);
    } else if (
      format === BlockDragAndDropFormats.BlockReorder &&
      getIsBlockReorderItem(data) &&
      !isAttachmentDragEvent(e)
    ) {
      const block = blocks.find((b) => b.key === (data as BlockReorderItem).key);

      if (block) {
        const direction = position > block.position ? 'down' : 'up';
        // the <CanvasDrop> directly before the block
        const samePosition = block.position === position;
        // the <CanvasDrop> directly after the block
        const sameDownPosition = direction === 'down' && block.position === position - 1;

        if (!samePosition && !sameDownPosition) {
          const newPosition = direction === 'down' ? position - 1 : position;

          setBlocks([
            ...moveBlock(
              {
                ...block,
                parentKey: parentKey || null,
                position: newPosition
              },
              blocks,
              direction
            )
          ]);
          setPublish(true);
        } else if (block.parentKey !== parentKey) {
          // moving a block out of or into a section without changing position
          setBlocks([...updateBlock({ ...block, parentKey: parentKey || null }, blocks)]);
          setPublish(true);
        }
      }

      setReordering(null);
    }
  };

  const disabled = publishing || (reordering?.type === BlockTypes.Section && !!parentKey);

  const { over } = useDrop({
    disabled,
    format: [
      BlockDragAndDropFormats.BlockDrag,
      BlockDragAndDropFormats.BlockReorder,
      BlockDragAndDropFormats.ImageDrag
    ],
    onDropped,
    ref
  });

  if (!editable || preview) return null;

  const isDragging = !disabled && (dragging || !!reordering);

  return (
    <div
      {...otherProps}
      className={classNames({
        'canvas-drop': true,
        'canvas-drop--dragging': isDragging,
        'canvas-drop--over': over,
        [`${otherProps?.className}`]: !!otherProps?.className
      })}
      ref={ref}
    >
      <p className="canvas-drop__text" style={{ backgroundColor: theme.body.backgroundColor }}>
        Drop here
      </p>
    </div>
  );
};

const StyledCanvasDrop = styled(UnstyledCanvasDrop)`
  ${drop}
  align-items: center;
  border-width: 1px;
  display: flex;
  height: 0;
  justify-content: center;
  margin: 12px 0;

  &:last-child {
    margin-bottom: 0;
  }

  &:not(.canvas-drop--color) {
    clear: both;
  }

  .canvas-drop__text {
    background-color: ${BrandfolderHexColors.White};
    color: ${BrandfolderHexColors.PurplePopsicle};
    font-family: 'Circular-Medium';
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    opacity: 0;
    padding: 0 12px;
    text-transform: uppercase;
    transition: opacity 0.1s ease;
  }

  &.canvas-drop--dragging {
    ${dropDragging}

    .canvas-drop__text {
      opacity: 1;
    }
  }

  &.canvas-drop--over {
    ${dropOver}
    height: 40px;

    .canvas-drop__text {
      background-color: transparent !important;
      opacity: 1;
    }
  }
`;

const CanvasDrop: FunctionComponent<CanvasDropProps> = (props) => {
  return <StyledCanvasDrop {...props} />;
};

export default CanvasDrop;
